import React from "react";
import {
  GridAggregationModel,
  GridColumnMenu,
  GridRowSelectionModel,
  useGridApiRef,
  isAutogeneratedRow,
} from "@mui/x-data-grid-premium";
import { MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

import { PaymentDocumentDTO } from "../../types/PaymentDocumentDTO";
import { createPaymentColumns } from "./columns";
import PaymentsToolbar from "./PaymentsToolbar";
import StripedDataGrid from "../common/StripedDataGrid";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { PaymentFilter } from "../../types/payment";
import { usePersistedGridState } from "../../hooks/usePersistedGridState";
import { applySavedColumnOrder } from "../../utils/gridStatePersistence";
import { OwnerOption } from "../documents/columns";

type Props = {
  gridStorageKey: string;

  rows: PaymentDocumentDTO[];
  loading: boolean;
  selectionModel: GridRowSelectionModel;
  onSelectionModelChange: (m: GridRowSelectionModel) => void;
  selectedCount: number;
  onGenerateFile: () => void;
  statusFilter: PaymentFilter;
  onStatusFilterChange: (status: PaymentFilter) => void;
  ownerOptions?: OwnerOption[];
};

function getAggregationModelFromInitialState(
  initialState: any
): GridAggregationModel {
  const model = initialState?.aggregation?.model;

  if (!model || typeof model !== "object") {
    return {};
  }

  return model;
}

type ClearAggregationMenuItemProps = {
  colDef?: {
    field?: string;
  };
  hideMenu?: (event: React.MouseEvent) => void;
  aggregationModel?: GridAggregationModel;
  onAggregationModelChange?: (model: GridAggregationModel) => void;
};

function ClearAggregationMenuItem(props: ClearAggregationMenuItemProps) {
  const {
    colDef,
    hideMenu,
    aggregationModel = {},
    onAggregationModelChange,
  } = props;

  const field = colDef?.field;

  if (!field || !aggregationModel[field]) {
    return null;
  }

  return (
    <MenuItem
      onClick={(event) => {
        event.stopPropagation();

        const nextModel = { ...aggregationModel };
        delete nextModel[field];

        onAggregationModelChange?.(nextModel);
        hideMenu?.(event);
      }}
    >
      <ListItemIcon>
        <ClearIcon fontSize="small" />
      </ListItemIcon>

      <ListItemText>Usuń agregację</ListItemText>
    </MenuItem>
  );
}

function PaymentsColumnMenu(props: any) {
  const {
    aggregationModel,
    onAggregationModelChange,
    ...columnMenuProps
  } = props;

  return (
    <GridColumnMenu
      {...columnMenuProps}
      slots={{
        columnMenuClearAggregationItem: ClearAggregationMenuItem as any,
      }}
      slotProps={{
        columnMenuClearAggregationItem: {
          displayOrder: 70,
          aggregationModel,
          onAggregationModelChange,
        } as any,
      }}
    />
  );
}

export default function PaymentsGrid(props: Props) {
  const {
    gridStorageKey,
    rows,
    loading,
    selectionModel,
    onSelectionModelChange,
    selectedCount,
    onGenerateFile,
    statusFilter,
    onStatusFilterChange,
    ownerOptions,
  } = props;

  const { t } = useAppTranslation("common");
  const apiRef = useGridApiRef();

  const { initialState, scheduleSave } = usePersistedGridState(
    apiRef,
    gridStorageKey
  );

  const [aggregationModel, setAggregationModel] =
    React.useState<GridAggregationModel>(() =>
      getAggregationModelFromInitialState(initialState)
    );

  const aggregationSaveReadyRef = React.useRef(false);

  React.useEffect(() => {
    aggregationSaveReadyRef.current = false;
    setAggregationModel(getAggregationModelFromInitialState(initialState));
  }, [gridStorageKey, initialState]);

  React.useEffect(() => {
    if (!aggregationSaveReadyRef.current) {
      aggregationSaveReadyRef.current = true;
      return;
    }

    scheduleSave();
  }, [aggregationModel, scheduleSave]);

  const handleAggregationModelChange = React.useCallback(
    (model: GridAggregationModel) => {
      setAggregationModel(model);
    },
    []
  );

  const baseColumns = React.useMemo(
    () => createPaymentColumns(t, ownerOptions),
    [t, ownerOptions]
  );

  const columns = React.useMemo(
    () => applySavedColumnOrder(baseColumns, initialState),
    [baseColumns, initialState]
  );

  const mergedInitialState = React.useMemo(
    () => ({
      ...initialState,
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25,
        },
        ...(initialState as any)?.pagination,
      },
    }),
    [initialState]
  );

  const isRealPaymentRow = React.useCallback((params: any) => {
    if (!params?.row) return false;
    if (isAutogeneratedRow(params.row)) return false;
    if (!params.row.systemId) return false;

    return true;
  }, []);

  const handleClearSelection = React.useCallback(() => {
    onSelectionModelChange({ type: "include", ids: new Set() });
  }, [onSelectionModelChange]);

  const handleScheduleSave = React.useCallback(() => {
    scheduleSave();
  }, [scheduleSave]);

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <StripedDataGrid
        key={gridStorageKey}
        apiRef={apiRef}
        initialState={mergedInitialState}
        showToolbar
        rows={rows}
        columns={columns}
        getRowId={(r) => r.systemId}
        getRowClassName={(params) => {
          if (isAutogeneratedRow(params.row)) {
            return "";
          }

          const { dueDate, paymentStatus } = params.row;

          if (!dueDate) return "";
          if (String(dueDate).startsWith("0001")) return "";

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const due = new Date(dueDate);
          const isOverdue = due < today;

          const normalizedPaymentStatus = paymentStatus?.toLowerCase();

          const isPaid =
            normalizedPaymentStatus === "paid" ||
            normalizedPaymentStatus === "opłacone";

          return isOverdue && !isPaid ? "row-overdue" : "";
        }}
        loading={loading}
        disableRowSelectionOnClick
        checkboxSelection
        isRowSelectable={isRealPaymentRow}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onSelectionModelChange}

        pagination
        paginationMode="client"
        pageSizeOptions={[10, 25, 50, 100]}

        aggregationModel={aggregationModel}
        onAggregationModelChange={handleAggregationModelChange}
        aggregationRowsScope="filtered"
        disableColumnPinning
        disablePivoting
        onPaginationModelChange={handleScheduleSave}
        onSortModelChange={handleScheduleSave}
        onFilterModelChange={handleScheduleSave}
        onColumnOrderChange={handleScheduleSave}
        onColumnWidthChange={handleScheduleSave}
        onColumnVisibilityModelChange={handleScheduleSave}
        onDensityChange={handleScheduleSave}
        onPreferencePanelClose={handleScheduleSave}
        slots={{
          toolbar: PaymentsToolbar as any,
          columnMenu: PaymentsColumnMenu as any,
        }}
        slotProps={{
          columnMenu: {
            aggregationModel,
            onAggregationModelChange: handleAggregationModelChange,
          } as any,
          toolbar: {
            title: t("Płatności"),
            total: rows.length,
            selectedCount,
            onGenerateFile,
            statusFilter,
            onStatusFilterChange,
            onClearSelection: handleClearSelection,
          } as any,
        }}
      />
    </div>
  );
}